/**
 * frontend/src/api/vendorTeam.js
 *
 * Client for the vendor team roles API (routes/vendorTeamRoutes.js, mounted
 * at /api/vendor-team). `vendorUserId` is always the OWNER's Users id; a team
 * member works for `user.parentVendorId`, an owner for their own `_id`.
 *
 * Every call goes through `dashboardApi`, the configured axios instance from
 * ./index.js, so the Bearer token, the active-country header and the silent
 * refresh interceptor all apply. Never swap in bare axios here.
 *
 * Contract: docs/commerce/vendor-team-roles.md, "Contract: team API".
 */
import { dashboardApi as API } from "./index.js";

const base = (vendorUserId) => `/api/vendor-team/${vendorUserId}`;

/**
 * The controllers may answer either `{ ...payload }` or `{ success, data: {...} }`.
 * Both shapes exist on this server, so every consumer unwraps through here
 * instead of guessing per call site.
 */
export const unwrapVendorTeam = (res) => {
  const body = res?.data;
  if (body && typeof body === "object" && body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  return body || {};
};

// Self: what this login may do for the vendor.
export const apiVendorTeamMyAccess = (vendorUserId) =>
  API.get(`${base(vendorUserId)}/me`);

// Catalogue: permission keys, roles with their defaults, hierarchy.
export const apiVendorTeamCatalogue = (vendorUserId) =>
  API.get(`${base(vendorUserId)}/catalogue`);

// Members (owner plus every helper, legacy helpers included).
export const apiVendorTeamMembers = (vendorUserId) =>
  API.get(`${base(vendorUserId)}/members`);

/**
 * The locations this login may act on, clamped to its branch access:
 * { vertical, parentId, locations: [{ id, name, address, isActive, isMain }],
 *   branchPricesEnabled }. `parentId` picks the brand for an owner of several.
 */
export const apiVendorTeamLocations = (vendorUserId, { parentId } = {}) =>
  API.get(`${base(vendorUserId)}/locations`, { params: parentId ? { parentId } : undefined });

/**
 * body: { firstName, lastName, email, phoneNumber?, roleKey, customPermissions?,
 *         branchScope?: { mode, outletIds, includeMain } }
 */
export const apiVendorTeamAddMember = (vendorUserId, body) =>
  API.post(`${base(vendorUserId)}/members`, body);

export const apiVendorTeamChangeRole = (vendorUserId, userId, roleKey) =>
  API.patch(`${base(vendorUserId)}/members/${userId}/role`, { roleKey });

/** customPermissions: [{ key, type: "GRANT" | "DENY", reason?, expiresAt? }] */
export const apiVendorTeamSetOverrides = (vendorUserId, userId, customPermissions) =>
  API.put(`${base(vendorUserId)}/members/${userId}/permissions`, { customPermissions });

export const apiVendorTeamSetStatus = (vendorUserId, userId, status) =>
  API.patch(`${base(vendorUserId)}/members/${userId}/status`, { status });

/**
 * Which branches a member works at (4 Oct 2026). body: { mode: "ALL"|"BRANCHES",
 * outletIds: string[], includeMain: boolean }. 400 INVALID_BRANCH_SCOPE (with
 * `invalid` ids), 404 MEMBER_NOT_FOUND, 409 OWNER_HAS_NO_SCOPE.
 */
export const apiVendorTeamSetBranches = (vendorUserId, userId, branchScope) =>
  API.put(`${base(vendorUserId)}/members/${userId}/branches`, branchScope);

export const apiVendorTeamRemoveMember = (vendorUserId, userId) =>
  API.delete(`${base(vendorUserId)}/members/${userId}`);

/**
 * Send the member a fresh set-password link (invite until they have signed in,
 * a password link after). Every earlier unredeemed link stops working. 429
 * with `retryAfter` inside the one-minute cooldown; 502 with the classified
 * mail fault (`code`, `message`) when the mail server refuses.
 */
export const apiVendorTeamResendInvite = (vendorUserId, userId) =>
  API.post(`${base(vendorUserId)}/members/${userId}/resend-invite`);

// Custom roles. body: { label, description?, permissions: [] }; the key is
// derived from the label on create and fixed after that.
export const apiVendorTeamCreateRole = (vendorUserId, body) =>
  API.post(`${base(vendorUserId)}/roles`, body);

export const apiVendorTeamUpdateRole = (vendorUserId, roleKey, body) =>
  API.put(`${base(vendorUserId)}/roles/${encodeURIComponent(roleKey)}`, body);

export const apiVendorTeamDeleteRole = (vendorUserId, roleKey) =>
  API.delete(`${base(vendorUserId)}/roles/${encodeURIComponent(roleKey)}`);

/** params: { page, limit, action, actorUserId, from, to } */
export const apiVendorTeamActivity = (vendorUserId, params) =>
  API.get(`${base(vendorUserId)}/activity`, { params });

// Name and email edits still go through the legacy helper route; the team API
// owns role, overrides, status and membership only.
export const apiVendorTeamUpdateMemberDetails = (userId, body) =>
  API.patch(`/seller/update-seller-helper/${userId}`, body);

// Read-only roster for a member WITHOUT team:manage (the members route above
// refuses them). The legacy list stays open to every helper of the vendor and
// now carries `roleKey` and `source` per helper.
export const apiVendorTeamLegacyHelpers = (vendorUserId) =>
  API.get(`/seller/get-seller-helpers/${vendorUserId}`);
